import { useEffect, useRef, useState } from 'react'
import { Button, Dialog } from '@/components/common'
import { PrepackSurfaceLiveTag } from './PrepackSurfaceLiveTag'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Where a removed pack stays listed (#1080): under its schedule's Packs, or,
// with no live schedule, under Schedules → Unscheduled packs.
const listedUnder = (schedule) => (schedule ? 'its schedule’s Packs' : 'Schedules → Unscheduled packs')

function SurfaceName({ option }) {
  return (
    <span className="min-w-0">
      <span className="block truncate font-medium">{option.name}{option.current ? ' (this surface)' : ''}</span>
      {option.known ? null : <span className="block truncate text-[10px] text-[var(--wf-color-text-muted)]">No longer a surface</span>}
    </span>
  )
}

/**
 * Remove on the Surfaces page (#999). Always asks first. A pack on other
 * surfaces too is offered this surface only, all of them, or a choice
 * (with this surface always included: the action started here). `target`
 * is { pack, options } with options from packSurfaceOptions; `schedule` is
 * the schedule that generated the pack, if any. `onRemove(slugs)` removes
 * the pack from those surfaces. Removing is not deleting: the pack stays
 * listed under its schedule's Packs, or under Schedules → Unscheduled packs
 * when no live schedule made it (#1080). `returnFocusRef` is the control that opened it, kept when
 * Remove is clicked, and `returnFocusFallbackRef` where focus goes instead
 * when the removal took that control off the page (#1054).
 */
export function RemoveFromSurfacesDialog({ target, schedule, busy, returnFocusRef, returnFocusFallbackRef, onRemove, onClose }) {
  const { pack, options } = target
  const current = options.find((option) => option.current) || options[0]
  const others = options.filter((option) => option !== current)
  const everySlug = options.map((option) => option.slug)
  const [step, setStep] = useState('ask')
  const [chosen, setChosen] = useState(() => new Set([current.slug]))
  // The button clicked shows the spinner; the others only disable.
  const [clicked, setClicked] = useState(null)
  const cancelRef = useRef(null)
  const backRef = useRef(null)
  // The overlay focuses the first control once, when the dialog opens; a
  // step change moves focus to the new step's first control here.
  useEffect(() => {
    (step === 'choose' ? backRef : cancelRef).current?.focus()
  }, [step])
  const remove = (choice, slugs) => { setClicked(choice); onRemove(slugs) }
  const toggle = (slug) => setChosen((set) => {
    const next = new Set(set)
    if (next.has(slug)) next.delete(slug)
    else next.add(slug)
    return next
  })
  const chosenSlugs = everySlug.filter((slug) => chosen.has(slug))
  const scheduleNote = schedule ? (
    <p className="text-xs text-[var(--wf-color-text-muted)]">Its schedule{schedule.name ? ` “${schedule.name}”` : ''} isn’t affected: its next runs still add new packs to its surfaces. Switch it off in Connected recipes to keep them off a surface.</p>
  ) : null
  const dialogProps = { open: true, onClose, dismissable: !busy, hideCloseButton: busy, returnFocusRef, returnFocusFallbackRef }

  if (others.length === 0) {
    return (
      <Dialog
        {...dialogProps}
        title={`Remove “${pack.title}” from ${current.name}?`}
        description={`It isn’t on any other surface, so it goes back to Draft. It stays listed under ${listedUnder(schedule)}.`}
        initialFocusRef={cancelRef}
        footer={(
          <>
            <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={() => remove('only', [current.slug])} loading={busy}>Remove from {current.name}</Button>
          </>
        )}
      >
        {scheduleNote}
      </Dialog>
    )
  }

  if (step === 'ask') {
    return (
      <Dialog
        {...dialogProps}
        title={`Remove “${pack.title}” from ${current.name}?`}
        description={`It is also on ${plural(others.length, 'other surface')}. Remove it from ${others.length === 1 ? 'that one' : 'those'} too? Either way it stays listed under ${listedUnder(schedule)}.`}
        initialFocusRef={cancelRef}
        footer={(
          <>
            <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="secondary" onClick={() => remove('only', [current.slug])} disabled={busy && clicked !== 'only'} loading={busy && clicked === 'only'}>Only {current.name}</Button>
            <Button variant="danger" onClick={() => remove('all', everySlug)} disabled={busy && clicked !== 'all'} loading={busy && clicked === 'all'}>Remove from all {options.length} surfaces</Button>
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          <ul aria-label="Other surfaces" className="flex flex-col gap-1 text-xs">
            {others.map((option) => (
              <li key={option.slug} className="flex items-center justify-between gap-2 rounded border border-[var(--wf-color-border)] px-3 py-2">
                <SurfaceName option={option} />
                <PrepackSurfaceLiveTag live={option.live} />
              </li>
            ))}
          </ul>
          <Button size="compact" variant="ghost" className="self-start" onClick={() => setStep('choose')} disabled={busy}>Choose surfaces…</Button>
          <p className="text-xs text-[var(--wf-color-text-muted)]">Removing it from every surface moves it back to Draft.</p>
          {scheduleNote}
        </div>
      </Dialog>
    )
  }

  const everyChosen = chosenSlugs.length === options.length
  return (
    <Dialog
      {...dialogProps}
      title={`Remove “${pack.title}” from which surfaces?`}
      description={`${current.name} is included: that is where you started.`}
      initialFocusRef={backRef}
      footer={(
        <>
          <Button ref={backRef} variant="secondary" onClick={() => setStep('ask')} disabled={busy}>Back</Button>
          <Button variant="danger" onClick={() => remove('chosen', chosenSlugs)} loading={busy}>Remove from {plural(chosenSlugs.length, 'surface')}</Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs">
          <button type="button" disabled={busy || everyChosen} onClick={() => setChosen(new Set(everySlug))} className="text-[var(--wf-color-action-text)] hover:underline disabled:opacity-40 disabled:no-underline">Select all</button>
          <span aria-hidden="true" className="text-[var(--wf-color-text-muted)]">·</span>
          <button type="button" disabled={busy || chosenSlugs.length === 1} onClick={() => setChosen(new Set([current.slug]))} className="text-[var(--wf-color-action-text)] hover:underline disabled:opacity-40 disabled:no-underline">Only {current.name}</button>
        </div>
        <ul aria-label="Surfaces" className="flex max-h-[320px] flex-col gap-1 overflow-y-auto text-xs">
          {options.map((option) => (
            <li key={option.slug}>
              <label className={`flex items-center justify-between gap-2 rounded border px-3 py-2 ${chosen.has(option.slug) ? 'border-[var(--wf-color-action)]' : 'border-[var(--wf-color-border)]'} ${option.current || busy ? '' : 'cursor-pointer'}`}>
                <span className="flex min-w-0 items-center gap-2.5">
                  <input type="checkbox" checked={chosen.has(option.slug)} disabled={busy || option.current} onChange={() => toggle(option.slug)} className="h-4 w-4 shrink-0" />
                  <SurfaceName option={option} />
                </span>
                <PrepackSurfaceLiveTag live={option.live} />
              </label>
            </li>
          ))}
        </ul>
        {everyChosen ? <p className="text-xs text-[var(--wf-color-warning)]">That is every surface it is on, so it goes back to Draft.</p> : null}
      </div>
    </Dialog>
  )
}
